import React from 'react'
import styled, { css } from 'styled-components'

import { ThemeColor } from 'theme/colors'
import { ThemeSpace } from 'theme/spaces'
import { Box, BoxProps } from 'components/styled/containers/box'

/*
This icon component can be used to render Material Design Icons.
All Material Design Icons can be found from: https://fonts.google.com/icons?selected=Material+Icons

Usage:
1. search icon from Material Design Icons page (e.g. "Mail Outline")
2. click the icon you want to use
3. from the right-hand-side menu copy the icon name (e.g. "mail_outline")
4. create icon component (e.g. <Icon type="mail_outline" size="s18" />)
*/

interface IconProps extends BoxProps {
  size?: ThemeSpace
  type: string
  color?: ThemeColor
  useOutlined?: boolean
  useSymbols?: boolean
  title?: string
  flipped?: boolean
}

const SymbolsStyle = css<{ useOutlined?: boolean }>`
  font-family: ${({ theme, useOutlined }) =>
    useOutlined
      ? theme.font.materialSymbolsOutlined
      : theme.font.materialSymbols};
`

const IconsStyle = css<{ useOutlined?: boolean }>`
  font-family: ${({ theme, useOutlined }) =>
    useOutlined ? theme.font.materialIconsOutlined : theme.font.materialIcons};
`

export const StyledIcon = styled(Box)<{
  size?: ThemeSpace
  color?: ThemeColor
  useOutlined?: boolean
  flipped?: boolean
  useSymbols?: boolean
}>`
  ${({ useSymbols }) => (useSymbols ? SymbolsStyle : IconsStyle)};
  font-weight: normal;
  font-style: normal;
  display: inline-block;
  line-height: 1;
  text-transform: none;
  letter-spacing: normal;
  word-wrap: normal;
  white-space: nowrap;
  direction: ltr;

  transform: ${({ flipped }) => flipped && 'scaleX(-1)'};

  /* Support for all WebKit browsers. */
  -webkit-font-smoothing: antialiased;
  /* Support for Safari and Chrome. */
  text-rendering: optimizeLegibility;

  /* Support for Firefox. */
  -moz-osx-font-smoothing: grayscale;

  /* Support for IE. */
  font-feature-settings: 'liga';

  font-size: ${({ size, theme }) =>
    size ? theme.space[size] : theme.space.s20};

  color: ${({ color, theme }) => color && theme.color[color]};
`

export const Icon: React.FC<IconProps> = ({ type, ...props }) => (
  <StyledIcon {...props}>{type}</StyledIcon>
)
